import React, { useEffect, useRef, useMemo } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import {
  pagesRoute,
  posRoutes,
  publicRoutes,
  onboardingRoute,
} from "./router.link";
import { Outlet } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import NetworkStatus from "../core/components/NetworkStatus";
import AppShell from "@/components/layout/AppShell";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { setcurrent_store } from "../core/redux/action";
import { all_routes } from "./all_routes";
import { ImpersonationHandler } from "../components/layout/ImpersonationHandler";

import RecordNotFound from "../core/components/RecordNotFound";

const RequirePermission = ({ permission, children }) => {
  const permissions = useSelector((state) => state.userpermission_data) || [];

  if (!permission) return children;

  const hasPermission = Array.isArray(permission)
    ? permission.some((p) => permissions.includes(p))
    : permissions.includes(permission);

  if (!hasPermission) {
    return <RecordNotFound />;
  }

  return children;
};

const HeaderLayout = () => {
  const token = useSelector((state) => state.bearer_token);

  // Guard: no token → send to sign-in, preserving the intended destination
  if (!token) {
    return <Navigate to={all_routes.signin} replace />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
};

// POS pages also require auth
const PosGuard = () => {
  const token = useSelector((state) => state.bearer_token);
  if (!token) {
    return <Navigate to={all_routes.signin} replace />;
  }
  return <Outlet />;
};

// Auth pages (sign-in, register, etc.) - redirect away if already authenticated
const Authpages = () => {
  const token = useSelector((state) => state.bearer_token);
  if (token) {
    return <Navigate to={all_routes.dashboard} replace />;
  }
  return (
    <div className="min-h-screen bg-background font-sans">
      <Outlet />
    </div>
  );
};

// Onboarding requires auth but renders without AppShell, and never redirects back to dashboard
const OnboardingGuard = () => {
  const token = useSelector((state) => state.bearer_token);
  if (!token) {
    return <Navigate to={all_routes.signin} replace />;
  }
  return (
    <div className="min-h-screen bg-background font-sans">
      <Outlet />
    </div>
  );
};

const AllRoutes = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.bearer_token);
  const currentStore = useSelector((state) => state.current_store);
  const permissionsData = useSelector((state) => state.userpermission_data);
  const permissions = useMemo(() => permissionsData || [], [permissionsData]);
  const permissionsRef = useRef(permissions);

  useEffect(() => {
    permissionsRef.current = permissions;
  }, [permissions]);

  // Fetch stores globally to ensure one is selected if none exists in redux
  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get("/store");
      return res.data.data || [];
    },
    enabled: !!token,
  });

  useEffect(() => {
    if (stores.length > 0 && !currentStore?.id) {
      dispatch(setcurrent_store(stores[0]));
    }
  }, [stores, currentStore, dispatch]);

  useEffect(() => {
    import("../core/utils/eventBus").then(({ eventBus }) => {
      const unsub = eventBus.subscribe("customer:created", async (data) => {
        try {
          const hasPermission =
            permissionsRef.current &&
            permissionsRef.current.includes("notifications email send");
          if (!hasPermission) return;
          if (!data.email_notifications) return;
          if (data.email === "" || data.email === "-") return;

          await apiClient.post(
            `/notifications/email/customer/${data.id}/registration`,
            {},
          );
        } catch (error) {
          console.error("Failed to send customer registration email:", error);
        }
      });
      return () => unsub();
    });
  }, []);

  return (
    <div>
      <NetworkStatus />
      <ImpersonationHandler />
      <Routes>
        <Route path="/pos" element={<PosGuard />}>
          {posRoutes.map((route, id) => (
            <Route
              path={route.path}
              element={
                route.permission ? (
                  <RequirePermission permission={route.permission}>
                    {route.element}
                  </RequirePermission>
                ) : (
                  route.element
                )
              }
              key={id}
            />
          ))}
        </Route>
        <Route path={"/"} element={<HeaderLayout />}>
          {publicRoutes.map((route, id) => (
            <Route
              path={route.path}
              element={
                route.permission ? (
                  <RequirePermission permission={route.permission}>
                    {route.element}
                  </RequirePermission>
                ) : (
                  route.element
                )
              }
              key={id}
            />
          ))}
        </Route>

        <Route path={"/"} element={<OnboardingGuard />}>
          {onboardingRoute.map((route, id) => (
            <Route path={route.path} element={route.element} key={id} />
          ))}
        </Route>

        <Route path={"/"} element={<Authpages />}>
          {pagesRoute.map((route, id) => (
            <Route path={route.path} element={route.element} key={id} />
          ))}
        </Route>

        {/* New Modern UI Routes */}
      </Routes>
    </div>
  );
};
export default AllRoutes;
